import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isYesterday, isFuture, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../hooks/useJobs';
import './Dashboard.css';

const statusConfig = {
  scheduled:    { icon: 'calendar', color: '#3b82f6', label: 'upcoming' },
  'in-progress':{ icon: 'calendar', color: '#3b82f6', label: 'active' },
  completed:    { icon: 'check',    color: '#10b981', label: 'done' },
  cancelled:    { icon: 'close',    color: '#ef4444', label: 'cancelled' },
};

const SectionHeader = ({ title }) => (
  <div className="dash-section-header">
    <span>{title}</span>
  </div>
);

const JobListCard = ({ job, onClick }) => {
  const cfg = statusConfig[job.status] || statusConfig.scheduled;
  const dateObj = job.scheduledDate ? parseISO(job.scheduledDate) : null;
  const isUpdated = job.updatedAt && job.createdAt && job.updatedAt !== job.createdAt;

  return (
    <div className="job-list-card" onClick={onClick} role="button" tabIndex={0}>
      <div className="job-list-left">
        <div className="job-list-icon" style={{ backgroundColor: cfg.color }}>
          {cfg.icon === 'calendar' && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          )}
          {cfg.icon === 'check' && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          )}
          {cfg.icon === 'close' && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          )}
        </div>
      </div>
      <div className="job-list-body">
        <div className="job-list-meta">
          <span className="job-list-date">
            {dateObj ? format(dateObj, 'dd/MM, HH:mm') : '—'}
          </span>
          {isUpdated && (
            <span className="job-list-updated">updated</span>
          )}
        </div>
        <h4 className="job-list-title">{job.title}</h4>
        {job.jobType && (
          <span className="job-list-type">{job.jobType}</span>
        )}
        <p className="job-list-location">
          {job.location}
          {job.distance && <span className="job-list-distance"> ({job.distance})</span>}
        </p>
        {job.jobReference && (
          <p className="job-list-ref">{job.jobReference}</p>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, profile, isAdmin, isManager } = useAuth();
  const { jobs, loading } = useJobs();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showSearch, setShowSearch] = useState(false);

  const myJobs = useMemo(() => {
    if (isAdmin || isManager) return jobs;
    return jobs.filter((j) => {
      // Support both old single-assignedTo and new multi-worker array
      if (Array.isArray(j.assignedTo)) {
        return j.assignedTo.includes(user?.uid);
      }
      return j.assignedTo === user?.uid;
    });
  }, [jobs, isAdmin, isManager, user]);

  const filteredJobs = useMemo(() => {
    let list = myJobs;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((j) =>
        (j.title || '').toLowerCase().includes(q) ||
        (j.location || '').toLowerCase().includes(q) ||
        (j.jobReference || '').toLowerCase().includes(q) ||
        (j.companyName || '').toLowerCase().includes(q)
      );
    }
    if (filterType !== 'all') {
      list = list.filter((j) => j.jobType === filterType);
    }
    return list;
  }, [myJobs, search, filterType]);

  // Group jobs chronologically
  const grouped = useMemo(() => {
    const upcoming = [];
    const today = [];
    const yesterday = [];
    const previous = [];

    for (const job of filteredJobs) {
      const d = job.scheduledDate ? parseISO(job.scheduledDate) : null;
      if (!d) { previous.push(job); continue; }

      if (isFuture(d) && !isToday(d)) {
        upcoming.push(job);
      } else if (isToday(d)) {
        today.push(job);
      } else if (isYesterday(d)) {
        yesterday.push(job);
      } else {
        previous.push(job);
      }
    }

    // Sort each group by scheduledDate ascending
    const sorter = (a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate);
    upcoming.sort(sorter);
    today.sort(sorter);
    yesterday.sort(sorter);
    previous.sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));

    return { upcoming, today, yesterday, previous };
  }, [filteredJobs]);

  // Unique job types for filter dropdown
  const jobTypes = useMemo(() => {
    const types = new Set(myJobs.map((j) => j.jobType).filter(Boolean));
    return ['all', ...Array.from(types)];
  }, [myJobs]);

  const handleJobClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  const handleNewJob = () => {
    navigate('/jobs');
  };

  return (
    <div className="page dashboard">
      {/* ─── Top App Bar ─── */}
      <div className="dash-appbar">
        <button className="dash-appbar-btn" onClick={() => {}} aria-label="Menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <h1 className="dash-appbar-title">All Jobs</h1>
        <button className="dash-appbar-btn" onClick={() => {}} aria-label="More options">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
        </button>
      </div>

      {/* ─── Search & Filters ─── */}
      <div className="dash-search-area">
        <div className="dash-search-row">
          <div className="dash-search-box">
            <svg className="dash-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              type="text"
              placeholder="SEARCH JOBS BY REFERENCE, LOCATION..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="dash-search-input"
            />
            {search && (
              <button className="dash-search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>
        </div>

        <div className="dash-filter-row">
          <button className="dash-filter-btn dash-filter-icon" onClick={() => setShowSearch((s) => !s)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
          </button>
          <button className="dash-filter-pill active">NEW/UPDATED</button>
          <select
            className="dash-filter-pill dash-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">JOB TYPE ▼</option>
            {jobTypes.filter((t) => t !== 'all').map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ─── Job List ─── */}
      <div className="dash-job-list">
        {loading ? (
          <div className="dash-loading">
            <div className="spinner" />
            <p>Loading jobs...</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="dash-empty">
            <p>No jobs found.</p>
            <p className="dash-empty-sub">
              {search ? 'Try a different search term.' : 'Your schedule is clear!'}
            </p>
          </div>
        ) : (
          <>
            {grouped.today.length > 0 && (
              <>
                <SectionHeader title="Today" />
                {grouped.today.map((job) => (
                  <JobListCard key={job.id} job={job} onClick={() => handleJobClick(job.id)} />
                ))}
              </>
            )}

            {grouped.upcoming.length > 0 && (
              <>
                <SectionHeader title="In the next days" />
                {grouped.upcoming.map((job) => (
                  <JobListCard key={job.id} job={job} onClick={() => handleJobClick(job.id)} />
                ))}
              </>
            )}

            {grouped.yesterday.length > 0 && (
              <>
                <SectionHeader title="Yesterday" />
                {grouped.yesterday.map((job) => (
                  <JobListCard key={job.id} job={job} onClick={() => handleJobClick(job.id)} />
                ))}
              </>
            )}

            {grouped.previous.length > 0 && (
              <>
                <SectionHeader title="Previous days" />
                {grouped.previous.map((job) => (
                  <JobListCard key={job.id} job={job} onClick={() => handleJobClick(job.id)} />
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* ─── FAB ─── */}
      {isAdmin && (
        <button className="dash-fab" onClick={handleNewJob} aria-label="New job">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
      )}
    </div>
  );
};

export default Dashboard;
